import { Spinner } from "@heroui/react";
import { useQuery } from "@tanstack/react-query";

import { errorMessage } from "@/lib/api";

import { listMyWorkLogs } from "./api";

const WEEKDAYS = ["日", "月", "火", "水", "木", "金", "土"];

/**
 * 作業日（YYYY-MM-DD）を "10/13(月)" にする。
 *
 * 文字列を分解して組むのは、`new Date("2026-10-13")` が UTC として解釈され、
 * 日本時間では前日に見えることがあるため。
 */
function formatWorkDate(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return ymd;
  return `${m}/${d}(${WEEKDAYS[new Date(y, m - 1, d).getDay()]})`;
}

/** 登録日時（ISO）を端末の時刻で "10/13 10:05" にする。 */
function formatCreatedAt(iso: string | null): string | null {
  if (iso == null) return null;
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return null;
  const hh = String(at.getHours()).padStart(2, "0");
  const mm = String(at.getMinutes()).padStart(2, "0");
  return `${at.getMonth() + 1}/${at.getDate()} ${hh}:${mm}`;
}

/** 時間を表示用に詰める（"2.50" → "2.5"、"1.00" → "1"）。 */
function formatHours(duration: string): string {
  const n = Number(duration);
  return Number.isFinite(n) ? String(n) : duration;
}

/**
 * 自分が登録した実績の直近 10 件。
 *
 * **二重登録に気づくための一覧**。現場では「さっき入れたか」が分からなくなるので、
 * 登録のたびにここへ出す。並びは作業日順ではなく**登録の新しい順**（API 側の並び）。
 * 作業日を遡って入れた実績が途中に埋もれると、入ったのかどうかが分からない。
 */
export default function RecentWorkLogs({ reloadKey }: { reloadKey: number }) {
  const { data, isPending, error } = useQuery({
    queryKey: ["work-logs", "mine", reloadKey],
    queryFn: () => listMyWorkLogs(),
  });

  return (
    <section className="flex flex-col gap-3 rounded-lg border border-default-200 bg-white p-4">
      <p className="text-sm font-bold text-default-700">最近の登録（自分）</p>

      {isPending ? (
        <div className="flex items-center gap-2 text-sm text-default-500">
          <Spinner size="sm" /> 読み込んでいます…
        </div>
      ) : error ? (
        <p className="text-sm text-danger">
          {errorMessage(error, "実績の取得に失敗しました。")}
        </p>
      ) : data == null || data.length === 0 ? (
        <p className="text-sm text-default-500">まだ登録がありません。</p>
      ) : (
        <ol className="flex flex-col">
          {data.map((log, index) => (
            <li key={log.work_log_id} className="flex gap-3">
              {/* 時系列の軸。最後の行は縦線を引かない。 */}
              <div className="flex flex-col items-center pt-1.5">
                <span className="size-2.5 shrink-0 rounded-full bg-brand-500" />
                {index < data.length - 1 && (
                  <span className="w-px flex-1 bg-default-200" />
                )}
              </div>

              <div className={index < data.length - 1 ? "flex-1 pb-4" : "flex-1"}>
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-bold text-default-800">
                    {formatWorkDate(log.work_date)}
                  </span>
                  <span className="text-sm font-bold text-default-800">
                    {formatHours(log.duration)} 時間
                  </span>
                  {formatCreatedAt(log.created_at) && (
                    <span className="ml-auto text-xs text-default-400">
                      登録 {formatCreatedAt(log.created_at)}
                    </span>
                  )}
                </div>
                <p className="text-sm text-default-600">
                  <span className="font-medium">{log.project_number}</span>
                  <span className="mx-1.5 text-default-300">/</span>
                  {log.drawing_number}
                  <span className="mx-1.5 text-default-300">/</span>
                  {log.process_name}
                  {log.machine_name && (
                    <>
                      <span className="mx-1.5 text-default-300">/</span>
                      <span className="text-xs">{log.machine_name}</span>
                    </>
                  )}
                </p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
